import { Component, HostBinding, Input } from '@angular/core';
import {
  STANDARD_DESKTOP_INPUT,
  STANDARD_DESKTOP_LABEL,
} from '../form-row/form-row.component';

/**
 * Read-only "label : value" display row used across the integration and
 * payment sections. Replaces the repeated
 *   <div class="row mt-2">
 *     <div class="col-md-3">{{ 'x' | translate }}</div>
 *     <div class="col-md-4">{{ value }}</div>
 *   </div>
 * boilerplate.
 *
 * Common case: pass `label` (translate key) + `value`. For richer values
 * (a link, a masked secret, an icon) project content instead of `value`:
 *   <app-detail-row label="admin.store.payment.spaceId">
 *     <a href="...">open</a>
 *   </app-detail-row>
 *
 * The host element IS the Bootstrap `.row`, so the rendered DOM matches the
 * original hand-written markup and the grid is unaffected.
 */
@Component({
  selector: 'app-detail-row',
  templateUrl: './detail-row.component.html',
  styleUrls: ['./detail-row.component.scss'],
})
export class DetailRowComponent {
  /** Translate key for the label (left column). */
  @Input() label = '';
  /** Plain value for the right column (skipped when content is projected). */
  @Input() value: string | number | null = null;
  /** Grid class(es) for the label column. Defaults to the admin-wide
   *  standard label width (same constant app-form-row standardizes to) so
   *  read-only rows and form rows on the same screen share one value edge —
   *  the old col-md-3 default left every payment screen's toggles indented
   *  relative to the static values above them (user 2026-08-19). */
  @Input() labelCol = STANDARD_DESKTOP_LABEL;
  /** Grid class(es) for the value column (the standard input column). */
  @Input() valueCol = STANDARD_DESKTOP_INPUT;
  /** Row wrapper classes — applied to the host, which IS the `.row`.
   *  Defaults to `row mb-4` (the standard field-row gap, matching
   *  app-form-row's built-in 1.5rem bottom margin), so the common case
   *  needs no override. */
  @Input() rowClass = 'row mb-4';

  @HostBinding('class')
  get hostClasses(): string {
    return this.rowClass;
  }

  get hasValue(): boolean {
    return this.value !== null && this.value !== undefined && this.value !== '';
  }
}
